import { useState, useEffect } from 'react'
import { useFormik } from 'formik'
import { Button, Flex, Text, TextField, Spinner } from '@radix-ui/themes'
import * as Yup from 'yup'
import api from '../../../api'
import SchoolSearch from '../../schoolSearch/SchoolSearch'
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify'
import { useDispatch } from 'react-redux'
import { updateStudents } from '../../../redux/slices/guardianSlice'

const AddWard = ({ onComplete }) => {
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(false)
    const [levels, setLevels] = useState([]);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [selectedSchoolId, setSelectedSchoolId] = useState(null);
    const account = useSelector(state => state.account);

    const formik = useFormik({
        initialValues: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            role: 'student',
            schoolName: '',
            level: selectedLevel,
        },
        validationSchema: Yup.object({
            firstName: Yup.string().required('First name is required'),
            lastName: Yup.string().required('Last name is required'),
            email: Yup.string()
                .email('Invalid email')
                // .required('Email is required')
                .notRequired(),
            role: Yup.string().required('Role is required'),
            level: Yup.string()
                .when('role', {
                    is: (value) => value === 'student',
                    then: () => Yup.string().required('Level is required'),
                    otherwise: () => Yup.string().notRequired()
                }),
            phone: Yup.string()
                .matches(/^[0-9]{7,10}$/, 'Phone number must be between 7 and 10 digits')
        }),
        onSubmit: async (values) => {
            try {
                formik.setStatus('')
                setLoading(true)
                const userData = {
                    firstname: values.firstName,
                    lastname: values.lastName,
                    role: values.role,
                    phone: values.phone,
                    email: values.email,
                    level: values.level,
                    school_id: selectedSchoolId,
                    guardian_id: account.details.id
                }
                console.log('submitted data', userData);

                const response = await api.post(`/guardians/add-student`, userData)
                const data = response.data
                console.log('adding student::', data);

                if (data.errors) {
                    formik.setErrors(data.errors)
                } else {
                    toast.success('Ward added successfully', {
                        position: "bottom-right"
                    })
                    dispatch(updateStudents(data.students))
                    formik.resetForm()
                    setSelectedLevel(null)
                    setSelectedSchoolId(null)
                    onComplete?.()
                }
            } catch (err) {
                console.log('check errors::', err)
                if (err.response.data.errors) {
                    formik.setErrors(err.response.data.errors)
                }
                formik.setStatus('An error occurred. Please try again.')
            } finally {
                setLoading(false)
            }
        }
    })


    useEffect(() => {
        if (formik.values.role === 'student') {
            const fetchLevels = async () => {
                console.log('Attempting to fetch levels');
                try {
                    const response = await api.get('/levels');
                    setLevels(response.data);
                } catch (error) {
                    console.error("Error fetching levels:", error);
                }
            };

            fetchLevels();
        }
    }, [formik.values.role]);

    const handleSelectSchool = (schoolId) => {
        if (selectedSchoolId === schoolId) {
            // Deselect if the same school is clicked
            setSelectedSchoolId(null);
            formik.setFieldValue('teacherSchoolId', '');
        } else {
            // Select the new school
            setSelectedSchoolId(schoolId);
            formik.setFieldValue('teacherSchoolId', schoolId);
        }
    };

    return (
        <div className='w-full'>
            <form onSubmit={formik.handleSubmit}>
                {formik.status && (
                    <Flex direction="column" justify="center" align="center">
                        <Text color="red" mb="4">{formik.status}</Text>
                    </Flex>
                )}

                <Flex direction="column" gap="3">
                    <TextField.Root size="3"
                        name="firstName"
                        value={formik.values.firstName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="First Name"
                    />
                    {formik.touched.firstName && formik.errors.firstName &&
                        <Text color="red" size="2">{formik.errors.firstName}</Text>
                    }

                    <TextField.Root size="3"
                        name="lastName"
                        value={formik.values.lastName}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Last Name"
                    />
                    {formik.touched.lastName && formik.errors.lastName &&
                        <Text color="red" size="2">{formik.errors.lastName}</Text>
                    }

                    <TextField.Root size="3"
                        name="email"
                        type="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Email Address (Optional)"
                    />
                    {formik.touched.email && formik.errors.email &&
                        <Text color="red" size="2">{formik.errors.email}</Text>
                    }

                    <TextField.Root size="3"
                        name="phone"
                        value={formik.values.phone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder="Phone Number (Optional)"
                    />
                    {formik.touched.phone && formik.errors.phone &&
                        <Text color="red" size="2">{formik.errors.phone}</Text>
                    }

                    <Flex direction="column" gap="2">
                        <Text size="2">
                            Select School <Text as="span">(Optional)</Text>
                        </Text>
                        <SchoolSearch
                            selectedSchoolId={selectedSchoolId}
                            onSelect={handleSelectSchool}
                        />
                    </Flex>

                    <Flex direction="column" gap="3" wrap="wrap" justify="center">
                        <Text size="2">Select a level</Text>
                        <Flex direction="row" gap="3" wrap="wrap" justify="center">
                            {levels.map((level) => (
                                <Button
                                    className='cursor-pointer'
                                    key={level.id}
                                    variant={selectedLevel === level.id ? 'solid' : 'soft'}
                                    onClick={(e) => {
                                        e.preventDefault()
                                        setSelectedLevel(level.id)
                                        formik.setValues({ ...formik.values, level: level.id })
                                    }}
                                >
                                    {level.label}
                                </Button>
                            ))}
                        </Flex>
                        {formik.touched.level && formik.errors.level &&
                            <Text color="red" size="2">{formik.errors.level}</Text>
                        }
                    </Flex>



                    <Button disabled={loading} type="submit" className='cursor-pointer'>
                        {loading ? (
                            <Flex gap="2" align="center">
                                <Spinner size="1" />
                                Loading...
                            </Flex>
                        ) : (
                            'Add Ward'
                        )}
                    </Button>


                </Flex>
            </form>
        </div>
    );
};

export default AddWard;